import React, { useState, useMemo } from 'react';
import { Button, Card, Row, Col, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaTrashAlt, FaAngleUp, FaAngleDown } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { default as Selector } from 'react-select';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { salvarLancamentoEmpreendimento, salvarEspecifico, apagarParametroEspecifico } from '~/Services/Service';
import { toast } from 'react-toastify';

const TelaLancamento = ({ empreendimentoId, optionsCorretores, lancamentoData, lancamentoEspecificoData, onRefresh }) => {
  const [ordenacao, setOrdenacao] = useState({ coluna: null, direcao: 'asc' });
  const [colunaHover, setColunaHover] = useState(null);

  const handleOrdenacao = coluna => {
    setOrdenacao(prev => ({
      coluna,
      direcao:
        prev.coluna === coluna && prev.direcao === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getIconeOrdenacao = coluna => {
    if (ordenacao.coluna !== coluna) return '';
    return ordenacao.direcao === 'asc' ? <FaAngleUp /> : <FaAngleDown />;
  };

  const dadosOrdenados = useMemo(() => {
    if (!lancamentoEspecificoData || !ordenacao.coluna) return lancamentoEspecificoData || [];

    return [...lancamentoEspecificoData].sort((a, b) => {
      const valorA = a[ordenacao.coluna];
      const valorB = b[ordenacao.coluna];

      if (typeof valorA === 'string') {
        const comparacao = valorA.localeCompare(valorB, 'pt-BR');
        return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
      }

      if (ordenacao.direcao === 'asc') {
        return valorA - valorB;
      }
      return valorB - valorA;
    });
  }, [lancamentoEspecificoData, ordenacao]);

  const tableStyles = {
    wrapper: {
      borderRadius: '8px',
      border: '1px solid #dee2e6',
      maxHeight: '300px',
      overflowY: 'auto',
    },
    header: {
      backgroundColor: '#0747a6',
      color: '#fff',
      borderColor: '#0747a6',
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'background-color 0.2s',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
    headerHover: {
      backgroundColor: '#053580',
    },
  };

  const colunasEspecifico = [
    { chave: 'usuario', label: 'Código' },
    { chave: 'descricao', label: 'Corretor' },
    { chave: 'prospecto', label: 'Prospecto' },
    { chave: 'tempo', label: 'Tempo' },
    { chave: 'limite', label: 'Limite' },
    { chave: 'limite_extra_fila_prospecto', label: 'Limite extra...', tooltip: 'Limite extra da fila de prospecto' },
  ];

  const formatDataParaInput = (data) => {
    if (!data) return '';
    if (typeof data === 'string' && data.includes('T')) {
      return data.split('T')[0];
    }
    return data;
  };

  const initialValues = {
    dataInicio:
      formatDataParaInput(lancamentoData && lancamentoData.data_inicio_lancamento) || '',
    dataFim: formatDataParaInput(lancamentoData && lancamentoData.data_fim_lancamento) || '',
    intervaloChamadas:
      (lancamentoData && lancamentoData.tempo_senhas) || 0,
    tempoEspera: (lancamentoData && lancamentoData.tempo_espera) || 0,
    tipoAtendimento:
      (lancamentoData && String(lancamentoData.tipo_atendimento)) || '1',
    tabelaResidencial:
      lancamentoData && lancamentoData.tabela_residencial ? String(lancamentoData.tabela_residencial) : '',
    limiteReservaProspecto:
      (lancamentoData && lancamentoData.limite_por_prospecto) || 0,
    tabelaComercial:
      lancamentoData && lancamentoData.tabela_comercial ? String(lancamentoData.tabela_comercial) : '',
    ultimaSenha: (lancamentoData && lancamentoData.ultima_senha_gerada) || 0,
    bloquearReserva:
      (lancamentoData && lancamentoData.bloqueia_reserva_corretor === 'S'
        ? 'sim'
        : 'nao') || 'nao',
    limiteAtendimento:
      (lancamentoData && lancamentoData.limite_atendimento) || 0,
    qtdProspectoAtivoInteresse:
      (lancamentoData && lancamentoData.qtd_prospecto_ativo_interesse) || 0,
  };

  const validationSchema = Yup.object().shape({
    dataInicio: Yup.date().nullable(),
    dataFim: Yup.date().nullable(),
    intervaloChamadas: Yup.number()
      .typeError('Deve ser um número')
      .min(0, 'Min 0')
      .integer('Inteiro'),
    tempoEspera: Yup.number()
      .typeError('Deve ser um número')
      .min(0, 'Min 0')
      .integer('Inteiro'),
    tipoAtendimento: Yup.string().required('Obrigatório'),
    tabelaResidencial: Yup.string()
      .matches(/^[0-9]*$/, 'Apenas números'),
    limiteReservaProspecto: Yup.number()
      .typeError('Deve ser um número')
      .min(0, 'Min 0')
      .integer('Inteiro'),
    tabelaComercial: Yup.string()
      .matches(/^[0-9]*$/, 'Apenas números'),
    ultimaSenha: Yup.number()
      .typeError('Deve ser um número')
      .min(0, 'Min 0')
      .integer('Inteiro'),
    bloquearReserva: Yup.string().required('Obrigatório'),
    limiteAtendimento: Yup.number()
      .typeError('Deve ser um número')
      .min(0, 'Min 0')
      .integer('Inteiro'),
    qtdProspectoAtivoInteresse: Yup.number()
      .typeError('Deve ser um número')
      .min(0, 'Min 0')
      .integer('Inteiro'),
  });

  const tipoAtendimentoOptions = [
    { value: '1', label: 'Aleatório' },
    { value: '2', label: 'Fila Corretor' },
    { value: '3', label: 'Fila Imobiliária' },
    { value: '4', label: 'Atendimento Colaborador' },
  ];

  const simNaoOptions = [
    { value: 'nao', label: 'Não' },
    { value: 'sim', label: 'Sim' },
  ];

  const handleSubmitConfig = async (values, { setSubmitting }) => {
    try {
      const payload = {
        familia: empreendimentoId,
        data_inicio_lancamento: values.dataInicio,
        data_fim_lancamento: values.dataFim,
        tempo_senhas: values.intervaloChamadas,
        tempo_espera: values.tempoEspera,
        tipo_atendimento: values.tipoAtendimento,
        tab_res: values.tabelaResidencial !== '' ? parseInt(values.tabelaResidencial, 10) : null,
        limite_por_prospecto: values.limiteReservaProspecto,
        tab_com: values.tabelaComercial !== '' ? parseInt(values.tabelaComercial, 10) : null,
        ultima_senha_gerada: values.ultimaSenha,
        bloqueia_reserva_corretor: values.bloquearReserva === 'sim' ? 'S' : 'N',
        limite_atendimento: values.limiteAtendimento,
        qtd_prospecto_ativo_interesse: values.qtdProspectoAtivoInteresse,
      };

      await salvarLancamentoEmpreendimento(payload);
      toast.success('Dados salvos com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar os dados.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEspecifico = async (values, { setSubmitting }) => {
    try {
      const payload = {
        familia: empreendimentoId,
        se_codigo: 0,
        usuario: values.corretor ? values.corretor.value : null,
        prospecto: values.prospecto,
        tempo: values.tempoMinutos,
        limite: values.limiteReserva,
        tempo_liberacao: values.tempoLiberacaoMinutos,
        limite_extra_fila_prospecto: values.limiteFilaProspecto,
      };

      await salvarEspecifico(payload);
      toast.success('Dados salvos com sucesso!');
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar os dados.');
    } finally {
      setSubmitting(false);
    }
  };

  const apagarEpecifico = async (seCodigo) => {
    try {
      await apagarParametroEspecifico(seCodigo);
      toast.success('Parâmetro apagado com sucesso!');
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Erro ao apagar:', error);
      toast.error('Erro ao apagar parâmetro.');
    }
  };

  return (
    <div className="bg-white my-3">
      <Header title="Lançamento" />
      <Row className="px-3 my-3">
        {/* Esquerda: Configurações de Lançamento */}
        <Col xs={12} xl={6} className="mb-3 mb-xl-0">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmitConfig}
          >
            {({ isSubmitting }) => (
              <FormikForm>
                <Card className="h-100">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">Configurações do lançamento de vendas</h6>
                  </Card.Header>
                  <Card.Body>
                    {/* Campos do formulário - grid responsivo */}
                    <Row>
                      {/* Coluna 1 */}
                      <Col xs={12} md={6}>
                        <div className="form-group mb-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Data inicial do lançamento</Tooltip>}
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>De</label>
                          </OverlayTrigger>
                          <Field
                            name="dataInicio"
                            type="date"
                            className="form-control form-control-sm"
                          />
                          <ErrorMessage
                            name="dataInicio"
                            component="div"
                            className="text-danger small"
                          />
                        </div>

                        <div className="form-group mb-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Tempo mínimo entre um atendimento e outro</Tooltip>}
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>Intervalo entre chamadas</label>
                          </OverlayTrigger>
                          <Field
                            name="intervaloChamadas"
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                          />
                          <ErrorMessage
                            name="intervaloChamadas"
                            component="div"
                            className="text-danger small"
                          />
                        </div>

                        <div className="form-group mb-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                Aleatório - Sem preferência de corretor.<br />
                                Fila do Corretor - Cliente será atendido pelo próprio corretor.<br />
                                Fila Imobiliária - Fila física dentro da imobiliária.<br />
                                Atendimento Colaborador - Modo atendimento pelo colaborador
                              </Tooltip>
                            }
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>Tipo Atendimento</label>
                          </OverlayTrigger>
                          <Field name="tipoAtendimento">
                            {({ field, form }) => (
                              <Selector
                                options={tipoAtendimentoOptions}
                                styles={customStyles}
                                components={{ DropdownIndicator }}
                                value={tipoAtendimentoOptions.find(
                                  option => option.value === field.value
                                )}
                                onChange={option =>
                                  form.setFieldValue(field.name, option.value)
                                }
                                onBlur={() =>
                                  form.setFieldTouched(field.name, true)
                                }
                                placeholder="Selecione..."
                              />
                            )}
                          </Field>
                          <ErrorMessage
                            name="tipoAtendimento"
                            component="div"
                            className="text-danger small"
                          />
                        </div>

                        <div className="form-group mb-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Quantidade máxima de lotes que o cliente pode demonstrar interesse</Tooltip>}
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>Qtd. Prospecto Ativo Interesse</label>
                          </OverlayTrigger>
                          <Field
                            name="qtdProspectoAtivoInteresse"
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                          />
                          <ErrorMessage
                            name="qtdProspectoAtivoInteresse"
                            component="div"
                            className="text-danger small"
                          />
                        </div>

                        <div className="form-group mb-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Usada apenas em LANÇAMENTO. Após o lançamento deve ficar sem dados nesse campo.</Tooltip>}
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>Tabela Residencial</label>
                          </OverlayTrigger>
                          <Field name="tabelaResidencial">
                            {({ field, form }) => (
                              <input
                                {...field}
                                type="text"
                                className="form-control form-control-sm"
                                placeholder=""
                                onChange={e => {
                                  var val = e.target.value.replace(/\D/g, '');
                                  form.setFieldValue(field.name, val);
                                }}
                              />
                            )}
                          </Field>
                          <ErrorMessage
                            name="tabelaResidencial"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </Col>

                      {/* Coluna 2 */}
                      <Col xs={12} md={6}>
                        <div className="form-group mb-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Data final do lançamento</Tooltip>}
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>Até</label>
                          </OverlayTrigger>
                          <Field
                            name="dataFim"
                            type="date"
                            className="form-control form-control-sm"
                          />
                          <ErrorMessage
                            name="dataFim"
                            component="div"
                            className="text-danger small"
                          />
                        </div>

                        <div className="form-group mb-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Congelamento por não-comparecimento</Tooltip>}
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>Tempo Espera</label>
                          </OverlayTrigger>
                          <Field
                            name="tempoEspera"
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                          />
                          <ErrorMessage
                            name="tempoEspera"
                            component="div"
                            className="text-danger small"
                          />
                        </div>

                        <div className="form-group mb-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Utilizado para que os auxiliares do lançamento não consumam todas as reservas em um único prospecto</Tooltip>}
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>Limite Reserva por Prospecto</label>
                          </OverlayTrigger>
                          <Field
                            name="limiteReservaProspecto"
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                          />
                          <ErrorMessage
                            name="limiteReservaProspecto"
                            component="div"
                            className="text-danger small"
                          />
                        </div>

                        <div className="form-group mb-2">
                          <label className="small mb-1">Última Senha Gerada</label>
                          <Field
                            name="ultimaSenha"
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                          />
                          <ErrorMessage
                            name="ultimaSenha"
                            component="div"
                            className="text-danger small"
                          />
                        </div>

                        <div className="form-group mb-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Usada apenas em LANÇAMENTO. Após o lançamento deve ficar sem dados nesse campo.</Tooltip>}
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>Tabela Comercial</label>
                          </OverlayTrigger>
                          <Field name="tabelaComercial">
                            {({ field, form }) => (
                              <input
                                {...field}
                                type="text"
                                className="form-control form-control-sm"
                                placeholder=""
                                onChange={e => {
                                  var val = e.target.value.replace(/\D/g, '');
                                  form.setFieldValue(field.name, val);
                                }}
                              />
                            )}
                          </Field>
                          <ErrorMessage
                            name="tabelaComercial"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </Col>
                    </Row>

                    <hr className="my-3" />

                    {/* Atendimento Colaborador */}
                    <h6 className="text-center mb-3">Atendimento Colaborador</h6>
                    <Row>
                      <Col xs={12} sm={6} className="mb-2 mb-sm-0">
                        <div className="form-group mb-0">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Bloquear reserva corretor. Não permite que corretores com mesa cadastrada no modo ATENDIMENTO COLABORADOR realizem reservas no PDV.</Tooltip>}
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>Bloquear Reserva</label>
                          </OverlayTrigger>
                          <Field name="bloquearReserva">
                            {({ field, form }) => (
                              <Selector
                                options={simNaoOptions}
                                styles={customStyles}
                                components={{ DropdownIndicator }}
                                value={simNaoOptions.find(
                                  option => option.value === field.value
                                )}
                                onChange={option =>
                                  form.setFieldValue(field.name, option.value)
                                }
                                onBlur={() =>
                                  form.setFieldTouched(field.name, true)
                                }
                                placeholder="Selecione..."
                              />
                            )}
                          </Field>
                          <ErrorMessage
                            name="bloquearReserva"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div className="form-group mb-0">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>ATENDIMENTO COLABORADOR. Limite de atendimento simultâneo para a regra do próximo atendimento aleatório</Tooltip>}
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>Limite de Atendimento</label>
                          </OverlayTrigger>
                          <Field
                            name="limiteAtendimento"
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                          />
                          <ErrorMessage
                            name="limiteAtendimento"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>

                  <Card.Footer className="d-flex flex-column flex-sm-row justify-content-end">
                    <button
                      type="submit"
                      className="btn btn-success btn-sm mb-2 mb-sm-0 mr-sm-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button type="button" className="btn btn-danger btn-sm">
                      Cancelar
                    </button>
                  </Card.Footer>
                </Card>
              </FormikForm>
            )}
          </Formik>
        </Col>

        {/* Direita: Encaminhamentos + Parâmetros Específicos */}
        <Col xs={12} xl={6}>
          <Card className="mb-3">
            <Card.Header className="bg-light py-2">
              <h6 className="mb-0" style={{ fontSize: '0.9rem' }}>Encaminhamentos</h6>
            </Card.Header>
            <Card.Body className="py-2 px-3">
              <Row className="mb-1">
                <Col xs={4} className="pr-1">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100"
                    onClick={() => window.open(`/lancamento/pre-encaminhamento?familia=${empreendimentoId}`, '_blank')}
                  >
                    Pré-Encaminhamento
                  </Button>
                </Col>
                <Col xs={4} className="px-1">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100"
                    onClick={() => window.open(`/lancamento/encaminhamento?familia=${empreendimentoId}`, '_blank')}
                  >
                    Encaminhamento
                  </Button>
                </Col>
                <Col xs={4} className="pl-1">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100"
                    onClick={() => window.open(`/lancamento/encaminhamento-assinatura?familia=${empreendimentoId}`, '_blank')}
                  >
                    Assinatura
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col xs={4} className="pr-1">
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="w-100"
                    onClick={() => window.open(`/lancamento/mesas?familia=${empreendimentoId}`, '_blank')}
                  >
                    Vincular Mesas
                  </Button>
                </Col>
                <Col xs={4} className="px-1">
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="w-100"
                    onClick={() => window.open('https://setpar-painel-senhas.netlify.app/?logo=https%3A%2F%2Fwww.setpar.com.br%2Fapp%2Fuploads%2F2023%2F06%2FlogoSetparque.webp&mesa=true&env=prod', '_blank')}
                  >
                    Monitor de Senhas
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <Formik
            initialValues={{
              corretor: '',
              prospecto: 0,
              tempoMinutos: 0,
              limiteReserva: 0,
              limiteFilaProspecto: 0,
              tempoLiberacaoMinutos: 0,
            }}
            onSubmit={handleSubmitEspecifico}
          >
            {({ isSubmitting, resetForm, handleSubmit, values, setFieldTouched, setFieldError }) => (
              <FormikForm>
                <Card>
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">Parâmetros (Específico)</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="form-group mb-2">
                      <label className="small mb-1">Corretor</label>
                      <Field name="corretor">
                        {({ field, form }) => (
                          <Selector
                            styles={customStyles}
                            components={{ DropdownIndicator }}
                            name="corretor"
                            options={optionsCorretores}
                            value={form.values.corretor}
                            isClearable={true}
                            onChange={selected =>
                              form.setFieldValue('corretor', selected)
                            }
                            placeholder="Todos..."
                          />
                        )}
                      </Field>
                    </div>

                    <Row>
                      <Col xs={6} md={6}>
                        <div className="form-group mb-2">
                          <label className="small mb-1">Prospecto</label>
                          <Field
                            name="prospecto"
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                          />
                          <ErrorMessage
                            name="prospecto"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </Col>
                      <Col xs={6} md={6}>
                        <div className="form-group mb-2">
                          <label className="small mb-1">Tempo (min)</label>
                          <Field
                            name="tempoMinutos"
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                          />
                          <ErrorMessage
                            name="tempoMinutos"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs={6} md={6}>
                        <div className="form-group mb-2">
                          <label className="small mb-1">Limite Reserva</label>
                          <Field
                            name="limiteReserva"
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                          />
                          <ErrorMessage
                            name="limiteReserva"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </Col>
                      <Col xs={6} md={6}>
                        <div className="form-group mb-2">
                          <label className="small mb-1">Limite Fila Reserva</label>
                          <Field
                            name="limiteFilaProspecto"
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                          />
                          <ErrorMessage
                            name="limiteFilaProspecto"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </Col>
                      <Col xs={6} md={6}>
                        <div className="form-group mb-2">
                          <label className="small mb-1">Tempo Liberação (min)</label>
                          <Field
                            name="tempoLiberacaoMinutos"
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                          />
                          <ErrorMessage
                            name="tempoLiberacaoMinutos"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>

                  <Card.Footer className="d-flex flex-column flex-sm-row justify-content-end">
                    <button
                      type="submit"
                      className="btn btn-success btn-sm mb-2 mb-sm-0 mr-sm-2"
                      disabled={isSubmitting}
                      onClick={(e) => {
                        e.preventDefault();
                        if (!values.corretor || !values.corretor.value) {
                          setFieldTouched('corretor', true);
                          if (setFieldError) setFieldError('corretor', 'Selecione um corretor');
                          toast.error('Selecione um corretor antes de salvar.');
                          return;
                        }
                        handleSubmit();
                      }}
                    >
                      {isSubmitting ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => resetForm()}
                    >
                      Limpar Dados
                    </button>
                  </Card.Footer>

                  <Card.Body className="pt-0">
                    <style>
                      {`
                        .tabela-lancamento tbody tr:hover {
                          background-color: #e8f4ff !important;
                        }
                        .tabela-lancamento thead th {
                          position: sticky;
                          top: 0;
                          z-index: 1;
                          background-color: #0747a6;
                        }
                        @media (max-width: 768px) {
                          .tabela-lancamento thead {
                            display: none;
                          }
                          .tabela-lancamento tbody tr {
                            display: block;
                            margin-bottom: 1rem;
                            border: 1px solid #dee2e6;
                            border-radius: 8px;
                            overflow: hidden;
                          }
                          .tabela-lancamento tbody td {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 0.75rem 1rem;
                            border: none;
                            border-bottom: 1px solid #eee;
                          }
                          .tabela-lancamento tbody td:last-child {
                            border-bottom: none;
                          }
                          .tabela-lancamento tbody td::before {
                            content: attr(data-label);
                            font-weight: 600;
                            color: #0747a6;
                            margin-right: 1rem;
                          }
                        }
                      `}
                    </style>
                    {dadosOrdenados && dadosOrdenados.length > 0 ? (
                      <div style={tableStyles.wrapper}>
                        <Table bordered hover size="sm" responsive className="mb-0 tabela-lancamento">
                          <thead>
                            <tr>
                              {colunasEspecifico.map(col => (
                                <th
                                  key={col.chave}
                                  title={col.tooltip || ''}
                                  style={{
                                    ...tableStyles.header,
                                    ...(colunaHover === col.chave
                                      ? tableStyles.headerHover
                                      : {}),
                                  }}
                                  onClick={() => handleOrdenacao(col.chave)}
                                  onMouseEnter={() => setColunaHover(col.chave)}
                                  onMouseLeave={() => setColunaHover(null)}
                                >
                                  {col.label}
                                  <span
                                    style={{ fontSize: '0.75rem', marginLeft: '4px' }}
                                  >
                                    {getIconeOrdenacao(col.chave)}
                                  </span>
                                </th>
                              ))}
                              <th style={tableStyles.header}>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dadosOrdenados.map((item) => (
                              <tr key={item.id}>
                                <td data-label="Código Usuário">{item.usuario}</td>
                                <td data-label="Corretor">{item.descricao}</td>
                                <td data-label="Prospecto">{item.prospecto}</td>
                                <td data-label="Tempo">{item.tempo}</td>
                                <td data-label="Limite">{item.limite}</td>
                                <td data-label="Limite Extra Fila Prospecto">{item.limite_extra_fila_prospecto}</td>
                                <td data-label="Ações" className="text-center">
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => apagarEpecifico(item.se_codigo)}
                                  >
                                    <FaTrashAlt />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="alert alert-secondary mb-0 small">
                        Nenhum usuário foi cadastrado até o momento.
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </FormikForm>
            )}
          </Formik>
        </Col>
      </Row>
    </div>
  );
};

export default TelaLancamento;
